import { IVector3 } from '../../api.js';
import { assert } from '../../babylon/utility/Assert.js';
import { SupportedObservers } from './DebugLayer.js';
import { DebugMenuController } from './web-elements/debug-menu/DebugMenu.js';
import { MoveGizmo } from './viewer-gizmos/move-gizmo/MoveGizmo.js';
import { RotateGizmo } from './viewer-gizmos/rotation-gizmo/RotateGizmo.js';
import { ScaleGizmo } from './viewer-gizmos/scale-gizmo/ScaleGizmo.js';
import { DebugTransformBarController, TransfomTool } from './web-elements/DebugTransformBar.js';
import { DescriptionManager } from './DescriptionManager.js';
import { has_required_keys } from '../HasRequiredKeys.js';
/**
 * Debug UiTools.
 *
 * Contains all UI Elements.
 * - Creates and updates `DebugMenu` and `DebugTransformBar`.
 * - Creates and updates UI Gizmos.
 */
export class UiTools {
    constructor(_viewer, _debug_layer) {
        this._viewer = _viewer;
        this._debug_layer = _debug_layer;
        this._active_gizmo = null;
        this._selected = null;
        this._custom_modules = new Map();
        this._config_tab = null;
        this._description_manager = new DescriptionManager(this._viewer, this._debug_layer);
        this._transform_bar = new DebugTransformBarController(_debug_layer.get_container());
        this._transform_bar.observables.on_active_changed.add(() => this.update_gizmo());
        this._transform_bar.observables.on_orientation_changed.add(() => this.update_gizmo());
        this._menu = new DebugMenuController(_debug_layer.get_container());
    }
    show() {
        this._description_manager.show_selectables();
        this._menu.show();
        this._transform_bar.show();
    }
    hide() {
        this.deselect();
        this._description_manager.hide_selectables();
        this._menu.hide();
        this._transform_bar.hide();
    }
    dispose() {
        this._description_manager.dispose();
        this._menu.hide();
        this._transform_bar.hide();
    }
    /**
     * Used by Decorators to define selectable types.
     *
     * @internal
     */
    define_selectable(...args) {
        this._description_manager.define_selectable(...args);
        this.build_config();
    }
    /**
     * Used by Decorators to define persistant types.
     *
     * @internal
     */
    define_persistent(...args) {
        this._description_manager.define_persistent(...args);
        this.build_config();
    }
    add_description(...args) {
        this._description_manager.describe(...args);
        this.build_config();
    }
    /**
     * Gets currently selected instance.
     *
     * @internal
     */
    get_selected() {
        return this._selected;
    }
    /**
     * Sets currently selected instance.
     *
     * Initiates all described tabs for the selected instance and updates Gizmos
     *
     * @internal
     */
    set_selected(selected) {
        var _a, _b, _c, _d;
        (_b = (_a = this._selected) === null || _a === void 0 ? void 0 : _a.on_deselect) === null || _b === void 0 ? void 0 : _b.call(_a);
        this._selected = selected;
        const { transform, on_select, initiate_tabs, instance } = selected;
        const active_tab = this._menu.api.get_active_tab();
        let to_activate_tab = null;
        this._menu.api.selection.clear();
        // Transform tab
        const tools = [];
        if (transform) {
            const transform_tab = this._menu.api.selection.get_tab('Transform');
            transform_tab.build((tab) => {
                if (transform.position) {
                    const { get, set } = transform.position;
                    tab.custom.watch('vector3', {
                        name: 'position',
                        get: get,
                        set: set
                    });
                    tools.push(TransfomTool.MOVE);
                }
                if (transform.rotation) {
                    const { get, set } = transform.rotation;
                    tab.custom.watch('vector3', {
                        name: 'rotation',
                        get: get,
                        set: set
                    });
                    if (transform.get_world_transform) {
                        tools.push(TransfomTool.ROTATE);
                    }
                }
                if (transform.scale) {
                    const { get, set } = transform.scale;
                    tab.custom.watch('vector3', {
                        name: 'scale',
                        get: get,
                        set: set
                    });
                    if (transform.position && transform.get_world_transform) {
                        tools.push(TransfomTool.SCALE);
                    }
                }
            });
            if (!to_activate_tab || (active_tab === null || active_tab === void 0 ? void 0 : active_tab.tag) === transform_tab.tag) {
                to_activate_tab = transform_tab;
            }
        }
        const show_orientation_switch = tools.length > 0 && !!((_c = this._selected.transform) === null || _c === void 0 ? void 0 : _c.get_world_transform) && !!((_d = this._selected.transform) === null || _d === void 0 ? void 0 : _d.get_parent_world_transform);
        this._transform_bar.set_available_tools(tools, show_orientation_switch);
        this.update_gizmo();
        // Other tabs
        initiate_tabs.forEach(initiator => {
            var _a;
            const tag = typeof initiator.tag === 'string' ? initiator.tag : initiator.tag(instance);
            const tab = this._menu.api.selection.get_tab(tag);
            if (initiator.custom_name) {
                tab.name = typeof initiator.custom_name === 'function'
                    ? initiator.custom_name(instance)
                    : initiator.custom_name;
            }
            tab.build((tab) => initiator.setup(tab, instance), (_a = initiator.dependencies) === null || _a === void 0 ? void 0 : _a.call(initiator, instance));
            if (!to_activate_tab || (active_tab === null || active_tab === void 0 ? void 0 : active_tab.tag) === tab.tag) {
                to_activate_tab = tab;
            }
        });
        if (to_activate_tab) {
            to_activate_tab.set_active();
        }
        on_select();
        this.update();
    }
    /**
     * Deselects current selection.
     *
     * Removes all selection tabs and Gizmos.
     */
    deselect() {
        var _a, _b;
        (_b = (_a = this._selected) === null || _a === void 0 ? void 0 : _a.on_deselect) === null || _b === void 0 ? void 0 : _b.call(_a);
        this._selected = null;
        this._transform_bar.set_available_tools([], false);
        this.update_gizmo();
        this._menu.api.set_active_tab(null);
        this._menu.api.selection.clear();
    }
    /**
     * Creates tabs for a persistent instance.
     *
     * @internal
     */
    persistent_tabs(instance, initiate_tabs) {
        const tabs = new Set();
        initiate_tabs.forEach(initiator => {
            var _a;
            const tag = typeof initiator.tag === 'string' ? initiator.tag : initiator.tag(instance);
            const tab = this._menu.api.persistent.get_tab(tag);
            if (initiator.custom_name) {
                tab.name = typeof initiator.custom_name === 'function'
                    ? initiator.custom_name(instance)
                    : initiator.custom_name;
            }
            tab.build((tab) => initiator.setup(tab, instance), (_a = initiator.dependencies) === null || _a === void 0 ? void 0 : _a.call(initiator, instance));
            tabs.add(tab);
        });
        return [...tabs];
    }
    /**
     * Creates a custom fully costumizeble tabs and defines them as a module in the config tab.
     */
    custom_module(tag, initiate_tabs, is_enabled = true) {
        assert(!this._custom_modules.has(tag), `Custom module with tag ${tag} already exists`);
        this._custom_modules.set(tag, { initiators: initiate_tabs, tabs: [], is_enabled: is_enabled });
        this.build_custom_modules();
        this.build_config();
    }
    update() {
        this.update_gizmo_position();
        this._description_manager.update();
        this._menu.api.update();
    }
    build_config() {
        if (this._config_tab) {
            this._config_tab.remove();
        }
        const tab = this._menu.api.config.get_tab('Config');
        const selectables = this._description_manager.get_selectable_handlers();
        const persistents = this._description_manager.get_persistent_handlers();
        tab.build((controller) => {
            controller.label('⚒ General');
            controller.custom.watch('checkbox', {
                name: 'render_on_demand',
                get: () => this._viewer.only_render_on_demand,
                set: (value) => { this._viewer.only_render_on_demand = value; }
            });
            let selected_observer = SupportedObservers.before_render;
            controller.custom.select('update watchers', Object.values(SupportedObservers), {
                get: () => selected_observer,
                set: (value) => {
                    selected_observer = value;
                    this._debug_layer.set_observer(value);
                }
            });
            if (selectables.length) {
                controller.label('⚙ Selectables');
                selectables.forEach(handler => {
                    const container = controller.container(handler.name, { is_collapsed: true });
                    container.custom.watch('checkbox', {
                        name: 'enabled',
                        get: () => handler.is_enabled(),
                        set: (value) => handler.set_enabled(value)
                    });
                    const descriptions = handler.get_applied_descriptions();
                    const decriptions_container = container.container('descriptions', { is_collapsed: true });
                    descriptions.forEach(description => {
                        decriptions_container.custom.watch('checkbox', {
                            name: description.description.name,
                            get: () => description.is_enabled(),
                            set: (value) => description.set_enabled(value)
                        });
                    });
                });
            }
            if (persistents.length) {
                controller.label('⚛ Persistents');
                persistents.forEach(handler => {
                    const container = controller.container(handler.name, { is_collapsed: true });
                    container.custom.watch('checkbox', {
                        name: 'enabled',
                        get: () => handler.is_enabled(),
                        set: (value) => handler.set_enabled(value)
                    });
                    const descriptions = handler.get_applied_descriptions();
                    const decriptions_container = container.container('descriptions', { is_collapsed: true });
                    descriptions.forEach(description => {
                        decriptions_container.custom.watch('checkbox', {
                            name: description.description.name,
                            get: () => description.is_enabled(),
                            set: (value) => description.set_enabled(value)
                        });
                    });
                });
            }
            if (this._custom_modules.size) {
                controller.label('Custom Modules');
                this._custom_modules.forEach((module, tag) => {
                    controller.custom.watch('checkbox', {
                        name: tag,
                        get: () => module.is_enabled,
                        set: (value) => {
                            if (value !== module.is_enabled) {
                                module.is_enabled = value;
                                this.build_custom_modules();
                            }
                        }
                    });
                });
            }
        });
        this._config_tab = tab;
    }
    build_custom_modules() {
        this._custom_modules.forEach((module, tag) => {
            module.tabs.forEach(tab => tab.remove());
            module.tabs.length = 0;
            if (module.is_enabled) {
                module.tabs.push(...module.initiators.map(initiator => {
                    var _a;
                    const tag = typeof initiator.tag === 'string' ? initiator.tag : initiator.tag();
                    const tab = this._menu.api.persistent.get_tab(tag);
                    tab.build((tab) => initiator.setup(tab), (_a = initiator.dependencies) === null || _a === void 0 ? void 0 : _a.call(initiator));
                    return tab;
                }));
            }
        });
    }
    update_gizmo() {
        var _a;
        (_a = this._active_gizmo) === null || _a === void 0 ? void 0 : _a.dispose();
        this._active_gizmo = null;
        if (this._selected) {
            if (this._transform_bar.active === TransfomTool.MOVE) {
                this.create_transform_gizmo(this._selected);
            }
            if (this._transform_bar.active === TransfomTool.ROTATE) {
                this.create_rotation_gizmo(this._selected);
            }
            if (this._transform_bar.active === TransfomTool.SCALE) {
                this.create_scale_gizmo(this._selected);
            }
            this.update_gizmo_position();
        }
    }
    update_gizmo_position() {
        if (this._active_gizmo) {
            if (this._selected) {
                this._active_gizmo.gizmo.position = this._selected.world_position();
            }
            this._active_gizmo.update_gizmo_rotation();
            this.update_gizmo_scale(this._active_gizmo.gizmo);
        }
    }
    create_transform_gizmo(selected) {
        assert(selected.transform && has_required_keys(selected.transform, 'position'), 'Move gizmo expected to have position handling');
        this._active_gizmo = new MoveGizmo(this._viewer, selected.transform, this._transform_bar.use_global_transform);
    }
    create_rotation_gizmo(selected) {
        assert(selected.transform && has_required_keys(selected.transform, 'rotation', 'get_world_transform'), 'Rotate gizmo expected to have rotation and world transform handling');
        this._active_gizmo = new RotateGizmo(this._viewer, selected.transform, this._transform_bar.use_global_transform);
    }
    create_scale_gizmo(selected) {
        assert(selected.transform && has_required_keys(selected.transform, 'position', 'scale', 'get_world_transform'), 'Scale gizmo expected to have position, scale and world transform handling');
        this._active_gizmo = new ScaleGizmo(this._viewer, selected.transform, this._transform_bar.use_global_transform);
    }
    update_gizmo_scale(gizmo) {
        const camera_position = this._viewer.get_camera_manager().engine_camera.position;
        const distance = IVector3.distance(camera_position, gizmo.position);
        const desired_size = 0.1;
        const scale = desired_size * distance;
        gizmo.scale = new IVector3(scale);
    }
}
//# sourceMappingURL=UiTools.js.map